// Only these two ids are forwarded to PostHog, so session replays and events
// can be filtered by the active agency and advertiser.
export const POSTHOG_ACCOUNT_ID_KEYS = ["agencyID", "advertiserID"];

export const POSTHOG_ACCOUNT_IDS_SENTRY_META = {
  functionName: "usePostHogAccountProperties",
  capture: "exception",
  tags: { operation: "posthog_account_properties" },
};

const isAccountID = (value) => typeof value === "string" && value.trim() !== "";

export const getPostHogAccountIDs = ({ agencyID, advertiserID } = {}) => {
  const ids = {};
  if (isAccountID(agencyID)) ids.agencyID = agencyID;
  if (isAccountID(advertiserID)) ids.advertiserID = advertiserID;
  return ids;
};

// True when AppRouter did not identify this uid (email/password login and
// custom-token reauth return early while isLoginInProgress()). After
// identify(), posthog-js get_distinct_id() is the uid, so a second call is skipped.
export const shouldIdentifyPostHogUser = ({ posthog, uid }) =>
  Boolean(
    posthog?.__loaded &&
      uid &&
      typeof posthog.get_distinct_id === "function" &&
      posthog.get_distinct_id() !== uid
  );

// role and companyID match AppRouter: role and userID from getPathDatabaseByRole.
// Account ids are the raw claims only. register runs before later captures so
// the first $pageview after login-as already has the super properties.
// group("company") is the same call AppRouter used to make on its own.
export const identifyPostHogUser = ({
  posthog,
  uid,
  role,
  companyID,
  agencyID,
  advertiserID,
}) => {
  const ids = getPostHogAccountIDs({ agencyID, advertiserID });
  if (Object.keys(ids).length > 0) posthog.register(ids);
  posthog.identify(uid, {
    role,
    companyID,
    ...ids,
  });
  if (isAccountID(companyID)) posthog.group("company", companyID);
};

const persistedAccountID = ({ posthog, key }) => {
  const value = posthog.get_property?.(key);
  return isAccountID(value) ? value : null;
};

const persistedMatchesIncomingIds = ({ posthog, ids }) =>
  POSTHOG_ACCOUNT_ID_KEYS.every((key) => {
    const persisted = persistedAccountID({ posthog, key });
    const incoming = ids[key];
    if (incoming) return persisted === incoming;
    return !persisted;
  });

// Registers the ids as super properties and drops stale ones. Person $set for
// a new distinct id is identifyPostHogUser. The same user's id changes update
// the person here. $unset reads persisted super properties, so a claim removed
// between sessions is cleared even when the in-memory ref was reset.
// Logout is covered by posthog.reset(). Returns the state to pass as `previous`.
export const syncPostHogAccountIDs = ({
  posthog,
  previous,
  uid,
  agencyID,
  advertiserID,
}) => {
  if (!uid) return null;

  const ids = getPostHogAccountIDs({ agencyID, advertiserID });

  if (!previous && persistedMatchesIncomingIds({ posthog, ids })) {
    return { uid, ids };
  }

  const isSameUser = previous?.uid === uid;
  const isUnchanged = POSTHOG_ACCOUNT_ID_KEYS.every(
    (key) => previous?.ids?.[key] === ids[key]
  );
  if (isSameUser && isUnchanged) return previous;

  const missingKeys = POSTHOG_ACCOUNT_ID_KEYS.filter((key) => !ids[key]);
  const unsetKeys = missingKeys.filter(
    (key) => persistedAccountID({ posthog, key }) || previous?.ids?.[key]
  );

  missingKeys.forEach((key) => posthog.unregister(key));
  if (Object.keys(ids).length > 0) posthog.register(ids);

  if (isSameUser && Object.keys(ids).length > 0) {
    posthog.setPersonProperties(ids);
  }

  // A different uid (login-as) must not $unset the previous person. A new
  // session has no in-memory ref, so the persisted super property is enough.
  if (unsetKeys.length > 0 && (isSameUser || !previous)) {
    posthog.capture("$set", { $unset: unsetKeys });
  }

  return { uid, ids };
};

// Single entry for auth (forceIdentify) and the Redux hook (identify when needed).
export const updatePostHogUserSession = ({
  posthog,
  previous,
  uid,
  role,
  companyID,
  agencyID,
  advertiserID,
  forceIdentify = false,
}) => {
  if (!uid) return null;

  const ids = getPostHogAccountIDs({ agencyID, advertiserID });
  let sessionPrevious = previous;

  const needsIdentify =
    role != null &&
    (forceIdentify || shouldIdentifyPostHogUser({ posthog, uid }));

  if (needsIdentify) {
    identifyPostHogUser({
      posthog,
      uid,
      role,
      companyID,
      agencyID,
      advertiserID,
    });
    sessionPrevious = { uid, ids };
  }

  return syncPostHogAccountIDs({
    posthog,
    previous: sessionPrevious ?? previous,
    uid,
    agencyID,
    advertiserID,
  });
};
